import { formatNumber, formatPercentage } from '@/utils/numberUtils';
import { formatMoneyWithCurrencyCode } from '../formatMoneyWithCurrencyCode';

export interface FormatChangeTextOptions {
    /**
     * Whether to include currency in the change value (e.g., "USD +2.34" vs "+2.34")
     * @default false
     */
    includeCurrency?: boolean;
    /**
     * Currency code to use when includeCurrency is true
     */
    currency?: string;
    /**
     * Number of decimal places for the change value
     * @default 2
     */
    decimalPlaces?: number;
}

/**
 * Returns the CSS class for change color based on the change value
 * @param change - The change value (positive or negative)
 * @returns CSS class string for green (positive) or red (negative) color
 */
export function getChangeColorClass(change: number): string {
    return change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
}

/**
 * Formats change value and percentage into a display string
 * @param change - The change value
 * @param changePercent - The change percentage (as decimal, e.g., 0.0128 for 1.28%)
 * @param options - Formatting options
 * @returns Formatted string like "+2.34 (+1.28%)" or "USD +2.34 (+1.28%)"
 */
export function formatChangeText(change: number, changePercent: number, options: FormatChangeTextOptions = {}): string {
    const { includeCurrency = false, currency, decimalPlaces = 2 } = options;
    const isPositive = change >= 0;

    const changeValue =
        includeCurrency && currency
            ? `${isPositive ? '+' : ''}${formatMoneyWithCurrencyCode(change, { currency })}`
            : `${isPositive ? '+' : ''}${formatNumber(change, { decimalPlaces })}`;

    const changePercentFormatted = formatPercentage(changePercent, { decimalPlaces: 2, trimZeros: false });

    return `${changeValue} (${isPositive ? '+' : ''}${changePercentFormatted})`;
}
